import { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { sendFile } from '../../../common/services/hold-document';
import { useToastContext } from '../../../common/components/ui/toast/toast.context';
import { HiOutlinePaperClip } from 'react-icons/hi2';
import { HiOutlineDownload } from 'react-icons/hi';
import { IoTrashBin } from 'react-icons/io5';

interface FileInputProps {
    onChange?: (file: string) => void;
    onDelete?: (file: string) => void;
    pathToUpload: string;
    resourceId?: string;
    resource?: string;
    name?: string;
    currentPath: string;
    nameFile?: string;
    canUpload?: boolean;
}

export const FileInputPropsInventoryManagement = forwardRef<HTMLInputElement, FileInputProps>(
    ({ onChange, onDelete, pathToUpload, nameFile, name, currentPath: argCurrentPath, canUpload = true }, ref) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const [currentPath, setCurrentPath] = useState(argCurrentPath);
        const { notifySuccess, notifyError } = useToastContext();
        const [showModal, setShowModal] = useState(false);

        useImperativeHandle(ref, () => inputRef.current!);

        const triggerFileSelect = () => inputRef.current?.click()

        const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files && event.target.files[0]) {
                const file = event.target.files[0]
                event.target.value = "";
                await handleSendPdf(file);
            }
        };

        async function handleSendPdf(file: File) {
            try {
                const prefix = pathToUpload;
                const data = {
                    prefix, file
                }

                if (name) {
                    Object.assign(data, { name })
                }

                const sendFileResponse = await sendFile(data);

                if (!sendFileResponse.ok) {
                    throw new Error(sendFileResponse.message || 'Erro ao enviar PDF');
                }

                const [{ key }] = sendFileResponse.data;

                if (onChange) {
                    onChange(key);
                    setCurrentPath(key);
                }
                notifySuccess(`PDF ${file.name} enviado com sucesso`);
            } catch (error) {
                notifyError(`Erro ao enviar o PDF ${file.name}: ${error}`);
            }
        }

        return (
            <div className="flex items-center gap-2">
                {!currentPath && canUpload ? (
                    <div className="custom-file-input">
                        <button type="button" className="m-2 bg-red text-white" onClick={triggerFileSelect}>
                            {nameFile}
                        </button>
                    </div>
                ) : ("")}
                <input
                    type="file"
                    ref={inputRef}
                    onChange={handleFileChange}
                    className="hidden"
                />
                {currentPath && (
                    <div className="flex items-center gap-2">
                        <a
                            href={`https://assets.locavibe.com.br/${currentPath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="col-span-1 flex items-center gap-3 bg-white border border-gray-200 p-3 rounded shadow-sm hover:bg-gray-50 transition"
                        >
                            <span className="p-2 rounded-full flex items-center justify-center">
                                <HiOutlinePaperClip className="h-5 w-5 text-black" />
                            </span>
                            <span className="flex-1 truncate text-blue-700 underline text-sm">
                                {currentPath.split("/").reverse()[0]}
                            </span>
                            <HiOutlineDownload className="h-4 w-4 text-gray-600" />
                        </a>
                        {canUpload && (
                            <button
                                type="button"
                                className="ml-2 p-2 rounded-full text-red shadow-lg hover:scale-105 transition-all duration-200 flex items-center justify-center"
                                onClick={() => setShowModal(true)}
                                title="Excluir arquivo"
                            >
                                <span>
                                    <IoTrashBin className="w-5 h-5" />
                                </span>
                            </button>
                        )}
                        {showModal && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-all duration-300">
                                <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full animate-fade-in">
                                    <div className="flex flex-col items-center">
                                        <div className="bg-red-100 rounded-full p-3 mb-4">
                                            <IoTrashBin className="w-8 h-8 text-red-600" />
                                        </div>
                                        <h2 className="text-xl font-bold mb-2 text-gray-800 text-center">Excluir arquivo?</h2>
                                        <p className="mb-6 text-gray-500 text-center text-sm">
                                            Tem certeza que deseja excluir este arquivo? Esta ação não poderá ser desfeita.
                                        </p>
                                        <div className="flex w-full justify-between gap-3 mt-2">
                                            <button
                                                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 transition"
                                                onClick={() => setShowModal(false)}
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                className="flex-1 px-4 py-2 rounded-lg font-semibold shadow transition"
                                                style={{ backgroundColor: '#E44131', color: 'white' }}
                                                onClick={() => {
                                                    onDelete?.(currentPath);
                                                    setCurrentPath("");
                                                    setShowModal(false);
                                                }}
                                            >
                                                Excluir
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    });
